import React from "react";
import PublicReservationLayout from "src/components/layouts/public-reservation";
import PrivateLayout from "src/components/layouts/private";
import { IRoute } from "src/interfaces";
import BaseHotelLayoutComponent from "src/components/layouts/hotel-layout";
import SallerProfileLayout from "src/components/layouts/saller-profile";
import ReservationListComponent from "src/pages/reservation-list";
import PublicResaleLayout from "src/components/layouts/public-resale";
import BaseResaleLayoutComponent from "src/components/layouts/resale-layout";
import ReservationSuccessComponent from "src/pages/reservation-success";
import { isTablet } from "react-device-detect";

const LoginComponent = React.lazy(() => import("src/pages/auth/login"));
const RegisterComponent = React.lazy(() => import("src/pages/auth/register"));
const ForgotPasswordComponent = React.lazy(
  () => import("src/pages/auth/forgotPassword")
);
const SetNewPasswordComponent = React.lazy(
  () => import("src/pages/auth/setNewPassword")
);
const HotelListComponent = React.lazy(() => import("src/pages/hotel-list"));
const HotelDetailComponent = React.lazy(() => import("src/pages/hotel-detail"));
const SallerProfileComponent = React.lazy(
  () => import("../pages/seller-profile")
);
const ResaleListDuringComponent = React.lazy(
  () => import("../pages/resale-list-during")
);
const ResaleDetailComponent = React.lazy(
  () => import("../pages/resale-detail")
);
const HotelReservationComponent = React.lazy(
  () => import("../pages/hotel-reservation-input")
);
const MyPageComponent = React.lazy(() => import("../pages/my-page"));
const ResaleFormPublicComponent = React.lazy(
  () => import("../pages/resale-form-public")
);
const ReservationDetailComponent = React.lazy(
  () => import("../pages/reservation-detail")
);
const ResaleBookingDetailComponent = React.lazy(
  () => import("../pages/booking-resale-detail")
);

const UserProfileComponent = React.lazy(() => import("../pages/user-profile"));
const SaleManagementPage = React.lazy(() => import("../pages/sale-management"));

const listNoAuthRouters: IRoute[] = [
  {
    path: "/",
    element: <PublicReservationLayout />,
    children: [
      {
        path: "auth",
        element: <BaseHotelLayoutComponent />,
        children: [
          {
            path: "login",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <LoginComponent />
              </React.Suspense>
            ),
          },
          {
            path: "register",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <RegisterComponent />
              </React.Suspense>
            ),
          },
          {
            path: "forgot-password",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ForgotPasswordComponent />
              </React.Suspense>
            ),
          },
          {
            path: "new-password",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <SetNewPasswordComponent />
              </React.Suspense>
            ),
          },
        ],
      },
      {
        path: "hotel",
        element: <BaseHotelLayoutComponent />,
        children: [
          {
            path: "list",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <HotelListComponent />
              </React.Suspense>
            ),
          },
          {
            path: "detail/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <HotelDetailComponent />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-input",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <HotelReservationComponent />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-input/success",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReservationSuccessComponent isResale={false} />
              </React.Suspense>
            ),
          },
        ],
      },
    ],
  },
];

const listResalesRouters: IRoute[] = [
  {
    path: "/",
    element: <PublicResaleLayout />,
    children: [
      {
        path: "resale",
        element: <BaseResaleLayoutComponent />,
        children: [
          {
            path: "list-during",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ResaleListDuringComponent />
              </React.Suspense>
            ),
          },
          {
            path: "detail/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ResaleDetailComponent />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-input/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <HotelReservationComponent isResale={true} />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-input/success",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReservationSuccessComponent isResale={true} />
              </React.Suspense>
            ),
          },
        ],
      },
    ],
  },
];

const sallerProfileRouters: IRoute[] = [
  {
    path: "/saller",
    element: <SallerProfileLayout />,
    children: [
      {
        path: "profile/:id",
        element: (
          <React.Suspense fallback={<div>Loading...</div>}>
            <SallerProfileComponent />
          </React.Suspense>
        ),
      },
    ],
  },
];

const listPrivateRouters: IRoute[] = [
  {
    path: "/authed",
    element: <PrivateLayout />,
    children: [
      {
        path: "resale",
        element: <BaseResaleLayoutComponent isHeader={true} />,
        children: [
          {
            path: "my-page",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <MyPageComponent isResale={true} />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-list",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReservationListComponent isResale={true} />
              </React.Suspense>
            ),
          },
          {
            path: "reservation/detail/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ResaleBookingDetailComponent />
              </React.Suspense>
            ),
          },
          {
            path: "to-form-public/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ResaleFormPublicComponent />
              </React.Suspense>
            ),
          },
        ],
      },
      {
        path: "hotel",
        element: <BaseHotelLayoutComponent isHeader={true} noBlur={isTablet} />,
        children: [
          {
            path: "my-page",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <MyPageComponent />
              </React.Suspense>
            ),
          },
          {
            path: "reservation-list",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReservationListComponent />
              </React.Suspense>
            ),
          },
          {
            path: "reservation/detail/:id",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <ReservationDetailComponent />
              </React.Suspense>
            ),
          },
        ],
      },
      {
        path: "my-page",
        element: <BaseHotelLayoutComponent isHeader={true} noBlur={isTablet} />,
        children: [
          {
            path: "user-profile",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <UserProfileComponent />
              </React.Suspense>
            ),
          },
          {
            path: "sale-management",
            element: (
              <React.Suspense fallback={<div>Loading...</div>}>
                <SaleManagementPage />
              </React.Suspense>
            ),
          },
        ],
      },
    ],
  },
];

export {
  listNoAuthRouters,
  listPrivateRouters,
  sallerProfileRouters,
  listResalesRouters,
};
